import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Users, ListTree, Boxes, DollarSign, Plus, Trash2, ChevronDown, ChevronRight, Pencil, Image as ImageIcon, Download, Bell, Send, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import NotificationCentre from "@/components/admin/NotificationCentre";
import BroadcastComposer from "@/components/admin/BroadcastComposer";
import FreeListingsManager from "@/components/admin/FreeListingsManager";
import CategoryMigrationReview from "@/components/admin/CategoryMigrationReview";
import CategoryPhotosManager from "@/components/admin/CategoryPhotosManager";
import SiteSettingsManager from "@/components/admin/SiteSettingsManager";
import { ListingForm } from "@/pages/DashboardPage";
import BuyerLeadsManager from "@/components/admin/BuyerLeadsManager";
import DeletionAuditPanel from "@/components/admin/DeletionAuditPanel";
import BootSummaryCard from "@/components/admin/BootSummaryCard";
import SafeModeRecoveryPanel from "@/components/admin/SafeModeRecoveryPanel";
import AutomationSwitchPanel from "@/components/admin/AutomationSwitchPanel";
import LoadErrorBanner, { describeLoadError } from "@/components/admin/LoadErrorBanner";
import OnboardingNudgePreview from "@/components/admin/OnboardingNudgePreview";
import EnquiriesManager from "@/components/admin/EnquiriesManager";
import FoundingOnboardingCheck from "@/components/admin/FoundingOnboardingCheck";
import SpotlightClicksTable from "@/components/admin/SpotlightClicksTable";
import FeaturedReportPanel from "@/components/admin/FeaturedReportPanel";
import TestimonialsManager from "@/components/admin/TestimonialsManager";
import CategoryInterestPanel from "@/components/admin/CategoryInterestPanel";
import VerificationQueue, { VerificationEditor, VerificationStatusPill } from "@/components/admin/VerificationQueue";

function CategoryManager() {
  const [tree, setTree] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [newParent, setNewParent] = useState("");
  const [newSubUnder, setNewSubUnder] = useState({});
  const [renaming, setRenaming] = useState({});
  const [imgEditing, setImgEditing] = useState({});

  const load = async () => {
    const { data } = await api.get("/categories/tree");
    setTree(data);
  };
  useEffect(() => { load(); }, []);

  const addParent = async (e) => {
    e.preventDefault();
    if (!newParent.trim()) return;
    try {
      await api.post("/categories", { name: newParent.trim(), parent_id: null });
      toast.success("Parent category added");
      setNewParent(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addSub = async (parentId) => {
    const name = (newSubUnder[parentId] || "").trim();
    if (!name) return;
    try {
      await api.post("/categories", { name, parent_id: parentId });
      toast.success("Subcategory added");
      setNewSubUnder({ ...newSubUnder, [parentId]: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const rename = async (cat) => {
    const newName = (renaming[cat.id] || "").trim();
    if (!newName) return;
    try {
      await api.put(`/categories/${cat.id}`, { name: newName, parent_id: cat.parent_id, description: cat.description || "", image_url: cat.image_url || "" });
      toast.success("Renamed");
      setRenaming({ ...renaming, [cat.id]: undefined }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveImage = async (cat) => {
    const url = (imgEditing[cat.id] || "").trim();
    try {
      await api.put(`/categories/${cat.id}`, { name: cat.name, parent_id: cat.parent_id, description: cat.description || "", image_url: url });
      toast.success("Image updated");
      setImgEditing({ ...imgEditing, [cat.id]: undefined }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Only allowed if no listings or subcategories are linked.`)) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-white border border-[#E8ECE9] p-6" data-testid="category-manager">
      <div className="font-serif text-xl text-[#11211A] mb-4">Category manager</div>

      <form onSubmit={addParent} className="flex gap-2 mb-6">
        <input value={newParent} onChange={(e) => setNewParent(e.target.value)} placeholder="New parent category" className="flex-1 px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="new-parent-input" />
        <button type="submit" className="btn-primary" data-testid="add-parent-btn"><Plus className="w-4 h-4" /> Add parent</button>
      </form>

      <div className="space-y-2">
        {tree.map((p) => (
          <div key={p.id} className="border border-[#E8ECE9]" data-testid={`cat-row-${p.id}`}>
            <div className="flex items-center gap-2 px-4 py-3 bg-[#F0EFEA]">
              <button onClick={() => setExpanded({ ...expanded, [p.id]: !expanded[p.id] })} className="text-[#4A5550]">
                {expanded[p.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              {renaming[p.id] !== undefined ? (
                <>
                  <input value={renaming[p.id]} onChange={(e) => setRenaming({ ...renaming, [p.id]: e.target.value })} className="flex-1 px-2 py-1 text-sm border border-[#C4CFC9] rounded-sm" />
                  <button onClick={() => rename(p)} className="text-xs text-[#264839]">Save</button>
                  <button onClick={() => setRenaming({ ...renaming, [p.id]: undefined })} className="text-xs text-[#7D8C85]">Cancel</button>
                </>
              ) : (
                <>
                  {p.image_url && <img src={p.image_url} alt="" className="w-8 h-8 object-cover" />}
                  <span className="font-serif text-lg text-[#11211A] flex-1">{p.name}</span>
                  <span className="text-xs text-[#7D8C85]">{(p.children || []).length} subs</span>
                  <button onClick={() => setImgEditing({ ...imgEditing, [p.id]: p.image_url || "" })} className="text-[#7D8C85] hover:text-[#264839] p-1" title="Edit image" data-testid={`edit-image-${p.id}`}><ImageIcon className="w-3 h-3" /></button>
                  <button onClick={() => setRenaming({ ...renaming, [p.id]: p.name })} className="text-[#7D8C85] hover:text-[#264839] p-1"><Pencil className="w-3 h-3" /></button>
                  <button onClick={() => del(p.id, p.name)} className="text-[#7D8C85] hover:text-red-600 p-1"><Trash2 className="w-3 h-3" /></button>
                </>
              )}
            </div>
            {imgEditing[p.id] !== undefined && (
              <div className="px-4 py-3 bg-white border-t border-[#E8ECE9] flex gap-2 items-center">
                <input
                  value={imgEditing[p.id]}
                  onChange={(e) => setImgEditing({ ...imgEditing, [p.id]: e.target.value })}
                  placeholder="Paste image URL (https://…)"
                  className="flex-1 px-3 py-1.5 text-sm border border-[#C4CFC9] rounded-sm"
                  data-testid={`image-url-input-${p.id}`}
                />
                <button onClick={() => saveImage(p)} className="text-xs text-[#264839] px-3 border border-[#264839] py-1.5" data-testid={`save-image-${p.id}`}>Save</button>
                <button onClick={() => setImgEditing({ ...imgEditing, [p.id]: undefined })} className="text-xs text-[#7D8C85]">Cancel</button>
              </div>
            )}
            {expanded[p.id] && (
              <div className="p-4 space-y-2">
                {(p.children || []).map((s) => (
                  <div key={s.id} className="flex items-center gap-2 pl-6 text-sm">
                    {renaming[s.id] !== undefined ? (
                      <>
                        <input value={renaming[s.id]} onChange={(e) => setRenaming({ ...renaming, [s.id]: e.target.value })} className="flex-1 px-2 py-1 text-sm border border-[#C4CFC9] rounded-sm" />
                        <button onClick={() => rename(s)} className="text-xs text-[#264839]">Save</button>
                        <button onClick={() => setRenaming({ ...renaming, [s.id]: undefined })} className="text-xs text-[#7D8C85]">Cancel</button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-[#4A5550]">{s.name}</span>
                        <button onClick={() => setRenaming({ ...renaming, [s.id]: s.name })} className="text-[#7D8C85] hover:text-[#264839] p-1"><Pencil className="w-3 h-3" /></button>
                        <button onClick={() => del(s.id, s.name)} className="text-[#7D8C85] hover:text-red-600 p-1"><Trash2 className="w-3 h-3" /></button>
                      </>
                    )}
                  </div>
                ))}
                <div className="flex gap-2 pl-6 pt-2">
                  <input
                    value={newSubUnder[p.id] || ""}
                    onChange={(e) => setNewSubUnder({ ...newSubUnder, [p.id]: e.target.value })}
                    placeholder="New subcategory"
                    className="flex-1 px-2 py-1.5 text-sm border border-[#C4CFC9] rounded-sm"
                    data-testid={`new-sub-input-${p.id}`}
                  />
                  <button onClick={() => addSub(p.id)} className="text-xs text-[#264839] px-3 border border-[#264839]" data-testid={`add-sub-btn-${p.id}`}>Add</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SubscribersManager() {
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = () => {
    setBusy(true);
    setLoadError(null);
    api.get("/admin/subscribers").then(({ data }) => setSubs(data)).catch((e) => { setSubs([]); setLoadError(describeLoadError(e, "subscribers")); }).finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, []);

  const del = async (s) => {
    if (!window.confirm(`Permanently delete subscriber ${s.email}?`)) return;
    setDeletingId(s.id);
    try {
      await api.delete(`/admin/subscribers/${s.id}`);
      toast.success(`Deleted ${s.email}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    } finally { setDeletingId(null); }
  };

  const exportCsv = () => {
    const rows = [["email", "role", "welcome_status", "created_at"]];
    subs.forEach((s) => rows.push([s.email, s.role, s.welcome_status || "", s.created_at]));
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `accommodation-suppliers-subscribers-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-[#E8ECE9] p-6" data-testid="subscribers-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-serif text-xl text-[#11211A]">Notify list ({subs.length})</div>
          <p className="text-xs text-[#7D8C85] mt-1">Buyers & suppliers who signed up to be notified when new categories launch.</p>
        </div>
        <button onClick={exportCsv} disabled={!subs.length} className="btn-ghost disabled:opacity-50" data-testid="export-subscribers-csv">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      <LoadErrorBanner error={loadError} onRetry={load} testId="admin-subscribers-error" />
      {busy ? <div className="text-sm text-[#7D8C85]">Loading…</div> : loadError ? null : subs.length === 0 ? (
        <div className="bg-[#F0EFEA] p-8 text-center text-[#7D8C85] rounded-sm">No subscribers yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8ECE9] text-left text-[#7D8C85]">
                <th className="py-2 px-2 font-medium">Email</th>
                <th className="py-2 px-2 font-medium">Role</th>
                <th className="py-2 px-2 font-medium">Welcome</th>
                <th className="py-2 px-2 font-medium">Signed up</th>
                <th className="py-2 px-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id} className="border-b border-[#F0EFEA]" data-testid={`subscriber-row-${s.id}`}>
                  <td className="py-2 px-2 text-[#11211A]">{s.email}</td>
                  <td className="py-2 px-2">
                    <span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 ${s.role === "admin" ? "bg-[#264839] text-white" : s.role === "advertiser" ? "bg-[#B35C4A] text-white" : "bg-[#F0EFEA] text-[#4A5550]"}`}>{s.role || "—"}</span>
                  </td>
                  <td className="py-2 px-2 text-xs text-[#7D8C85]">{s.welcome_status || "—"}</td>
                  <td className="py-2 px-2 text-xs text-[#7D8C85]">{new Date(s.created_at).toLocaleDateString()}</td>
                  <td className="py-2 px-2 text-right">
                    <button
                      onClick={() => del(s)}
                      disabled={deletingId === s.id}
                      className="text-[10px] uppercase tracking-[0.15em] px-3 py-1 border border-[#B35C4A] text-[#B35C4A] bg-white hover:bg-[#B35C4A] hover:text-white transition-colors disabled:opacity-50"
                      data-testid={`subscriber-delete-${s.id}`}
                    >Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CategoryLeadsManager() {
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(true);
  const [filter, setFilter] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = () => {
    setBusy(true);
    setLoadError(null);
    api.get("/admin/category-leads").then(({ data }) => setLeads(data)).catch((e) => { setLeads([]); setLoadError(describeLoadError(e, "category leads")); }).finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, []);

  const del = async (l) => {
    if (!window.confirm(`Permanently delete lead ${l.email} (${l.category_name})?`)) return;
    setDeletingId(l.id);
    try {
      await api.delete(`/admin/category-leads/${l.id}`);
      toast.success(`Deleted lead ${l.email}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    } finally { setDeletingId(null); }
  };

  const filtered = filter === "" ? leads : leads.filter((l) => l.category_name === filter);
  const categories = Array.from(new Set(leads.map((l) => l.category_name))).sort();

  const exportCsv = () => {
    const rows = [["category", "property_name", "location", "email", "email_status", "created_at"]];
    filtered.forEach((l) => rows.push([l.category_name, l.property_name, l.location, l.email, l.email_status || "", l.created_at]));
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `category-leads-${filter || "all"}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-[#E8ECE9] p-6" data-testid="category-leads-manager">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="font-serif text-xl text-[#11211A]">Coming Soon category leads ({filtered.length}{filter ? ` / ${leads.length}` : ""})</div>
          <p className="text-xs text-[#7D8C85] mt-1">Buyers who signed up via the "Notify Me" form on Coming Soon category tiles. Perfect launch list.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="text-xs border border-[#C4CFC9] px-2 py-1.5 rounded-sm bg-white" data-testid="category-leads-filter">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={exportCsv} disabled={!filtered.length} className="btn-ghost disabled:opacity-50" data-testid="export-category-leads-csv">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>
      <LoadErrorBanner error={loadError} onRetry={load} testId="admin-category-leads-error" />
      {busy ? <div className="text-sm text-[#7D8C85]">Loading…</div> : loadError ? null : filtered.length === 0 ? (
        <div className="bg-[#F0EFEA] p-8 text-center text-[#7D8C85] rounded-sm">
          {leads.length === 0 ? "No leads yet. Buyers who click a Coming Soon tile will appear here." : "No leads for this category yet."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8ECE9] text-left text-[#7D8C85]">
                <th className="py-2 px-2 font-medium">Category</th>
                <th className="py-2 px-2 font-medium">Property</th>
                <th className="py-2 px-2 font-medium">Location</th>
                <th className="py-2 px-2 font-medium">Email</th>
                <th className="py-2 px-2 font-medium">Notify</th>
                <th className="py-2 px-2 font-medium">Signed up</th>
                <th className="py-2 px-2 font-medium">Role</th>
                <th className="py-2 px-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} className="border-b border-[#F0EFEA]" data-testid={`lead-row-${l.id}`}>
                  <td className="py-2 px-2"><span className="text-[10px] uppercase tracking-[0.15em] bg-[#F0EFEA] text-[#264839] px-2 py-0.5">{l.category_name}</span></td>
                  <td className="py-2 px-2 text-[#11211A]">{l.property_name}</td>
                  <td className="py-2 px-2 text-[#4A5550]">{l.location}</td>
                  <td className="py-2 px-2 text-[#11211A]">{l.email}</td>
                  <td className="py-2 px-2 text-xs text-[#7D8C85]">{l.email_status || "—"}</td>
                  <td className="py-2 px-2 text-xs text-[#7D8C85]">{new Date(l.created_at).toLocaleDateString()}</td>
                  <td className="py-2 px-2">
                    <span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 ${l.role === "admin" ? "bg-[#264839] text-white" : l.role === "advertiser" ? "bg-[#B35C4A] text-white" : "bg-[#F0EFEA] text-[#4A5550]"}`}>{l.role || "—"}</span>
                  </td>
                  <td className="py-2 px-2 text-right">
                    <button
                      onClick={() => del(l)}
                      disabled={deletingId === l.id}
                      className="text-[10px] uppercase tracking-[0.15em] px-3 py-1 border border-[#B35C4A] text-[#B35C4A] bg-white hover:bg-[#B35C4A] hover:text-white transition-colors disabled:opacity-50"
                      data-testid={`lead-delete-${l.id}`}
                    >Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");
  const [unread, setUnread] = useState(0);
  const [pendingVerifs, setPendingVerifs] = useState(0);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { navigate("/login"); return; }
    api.get("/admin/stats").then(({ data }) => setStats(data));
  }, [user, loading, navigate]);

  // Iter62 · poll unread notification count every 60s for the red badge.
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/notifications/unread-count");
        if (alive) setUnread(data.unread_count || 0);
      } catch (_) { /* noop */ }
      try {
        const { data } = await api.get("/admin/verifications/pending-count");
        if (alive) setPendingVerifs(data.pending || 0);
      } catch (_) { /* noop */ }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [user]);

  if (!stats) return <div className="max-w-7xl mx-auto px-4 py-16">Loading…</div>;

  const cards = [
    { label: "Total suppliers", value: stats.suppliers ?? stats.advertisers, icon: Users },
    { label: "Total listings", value: stats.listings, icon: Boxes },
    { label: "Categories", value: stats.categories, icon: ListTree },
    { label: "Revenue (USD)", value: `$${stats.revenue_usd?.toFixed?.(2) || 0}`, icon: DollarSign },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-page">
      <div className="tracking-overline mb-3">Admin</div>
      <h1 className="font-serif text-4xl text-[#11211A]">Platform overview</h1>

      <div className="flex gap-1 border-b border-[#E8ECE9] mt-8 mb-6 flex-wrap">
        <button onClick={() => setTab("overview")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "overview" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-overview">Overview</button>
        <button onClick={() => setTab("notifications")} className={`px-4 py-2 text-sm border-b-2 -mb-px inline-flex items-center gap-1.5 ${tab === "notifications" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-notifications">
          <Bell className="w-3.5 h-3.5" /> Notifications
          {unread > 0 && (
            <span className="bg-[#B35C4A] text-white text-[10px] px-1.5 py-0.5 rounded-full font-semibold tabular-nums leading-none" data-testid="admin-nav-notif-badge">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
        <button onClick={() => setTab("broadcast")} className={`px-4 py-2 text-sm border-b-2 -mb-px inline-flex items-center gap-1.5 ${tab === "broadcast" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-broadcast">
          <Send className="w-3.5 h-3.5" /> Broadcast
        </button>
        <button onClick={() => setTab("listings")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "listings" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-listings">
          Listings
        </button>
        <button onClick={() => setTab("verification")} className={`px-4 py-2 text-sm border-b-2 -mb-px inline-flex items-center gap-1.5 ${tab === "verification" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-verification">
          <BadgeCheck className="w-3.5 h-3.5" /> Verification
          {pendingVerifs > 0 && (
            <span className="bg-[#B8860B] text-white text-[10px] px-1.5 py-0.5 rounded-full font-semibold tabular-nums leading-none" data-testid="admin-verification-pending-badge">
              {pendingVerifs > 99 ? "99+" : pendingVerifs}
            </span>
          )}
        </button>
        <button onClick={() => setTab("refunds")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "refunds" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-refunds">
          Refunds
        </button>
        <button onClick={() => setTab("categories")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "categories" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-categories">Categories</button>
        <button onClick={() => setTab("category-photos")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "category-photos" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-category-photos">Category photos</button>
        <button onClick={() => setTab("subscribers")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "subscribers" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-subscribers">Subscribers</button>
        <button onClick={() => setTab("category-leads")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "category-leads" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-category-leads">Category Leads</button>
        <button onClick={() => setTab("buyer-leads")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "buyer-leads" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-buyer-leads">Buyer Leads</button>
        <button onClick={() => setTab("users")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "users" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-users">Users</button>
        <button onClick={() => setTab("founding")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "founding" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-founding">Founding Suppliers</button>
        <button onClick={() => setTab("free-listings")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "free-listings" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-free-listings">Free Listings</button>
        <button onClick={() => setTab("enquiries")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "enquiries" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-enquiries">Enquiries</button>
        <button onClick={() => setTab("testimonials")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "testimonials" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-testimonials">Testimonials</button>
        <button onClick={() => setTab("deletion-audit")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "deletion-audit" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-deletion-audit">Deletion Audit</button>
        <button onClick={() => setTab("settings")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "settings" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="admin-tab-settings">Settings</button>
        <Link to="/admin/email-preview" className="px-4 py-2 text-sm border-b-2 -mb-px border-transparent text-[#7D8C85] hover:text-[#264839]" data-testid="admin-nav-email-preview">Email gallery ↗</Link>
      </div>

      {tab === "overview" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {cards.map((c) => (
              <div key={c.label} className="bg-white border border-[#E8ECE9] p-6" data-testid={`stat-${c.label}`}>
                <c.icon className="w-5 h-5 text-[#264839]" />
                <div className="mt-4 font-serif text-3xl text-[#11211A]">{c.value}</div>
                <div className="text-xs text-[#7D8C85] mt-1">{c.label}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <button type="button" onClick={() => setTab("enquiries")} className="text-left bg-[#11211A] text-white p-6 border-l-4 border-[#F0C866] hover:bg-[#1A2F26] transition" data-testid="stat-campaign-rfqs">
              <div className="flex items-center justify-between">
                <div className="tracking-overline text-[10px] text-[#F0C866]">/get-quotes campaign</div>
                <Send className="w-4 h-4 text-[#F0C866]" />
              </div>
              <div className="mt-3 font-serif text-4xl" data-testid="stat-campaign-rfqs-total">{stats.campaign_rfqs ?? 0}</div>
              <div className="text-xs text-white/70 mt-1">RFQs received from the campaign page · <span data-testid="stat-campaign-rfqs-7d">{stats.campaign_rfqs_last_7d ?? 0}</span> in the last 7 days · <span data-testid="stat-campaign-rfqs-replied">{stats.campaign_rfqs_replied ?? 0}</span> replied</div>
              <div className="text-[10px] uppercase tracking-widest text-[#F0C866] mt-3">Open in Enquiries →</div>
            </button>
            <div className="bg-white border border-[#E8ECE9] p-6">
              <div className="font-serif text-xl text-[#11211A]">Paid transactions</div>
              <div className="text-3xl mt-2 font-serif">{stats.paid_transactions}</div>
              <p className="text-sm text-[#7D8C85] mt-2">Total successful Stripe payments processed by suppliers.</p>
            </div>
          </div>
          <AutomationSwitchPanel />
          <BootSummaryCard />
          <OnboardingNudgePreview />
          <SafeModeRecoveryPanel />
        </>
      )}

      {tab === "notifications" && <NotificationCentre />}
      {tab === "broadcast" && <BroadcastComposer />}
      {tab === "listings" && <ListingsManager />}
      {tab === "verification" && <VerificationQueue onChanged={() => api.get("/admin/verifications/pending-count").then(({ data }) => setPendingVerifs(data.pending || 0)).catch(() => {})} />}
      {tab === "refunds" && <RefundsManager />}
      {tab === "categories" && (<><CategoryMigrationReview /><CategoryManager /></>)}
      {tab === "category-photos" && <CategoryPhotosManager />}
      {tab === "subscribers" && <SubscribersManager />}
      {tab === "category-leads" && (<><CategoryInterestPanel /><CategoryLeadsManager /></>)}
      {tab === "buyer-leads" && <BuyerLeadsManager />}
      {tab === "users" && <UsersManager />}
      {tab === "founding" && <FoundingSuppliersManager />}
      {tab === "free-listings" && <FreeListingsManager />}
      {tab === "enquiries" && <EnquiriesManager />}
      {tab === "testimonials" && <TestimonialsManager />}
      {tab === "deletion-audit" && <DeletionAuditPanel />}
      {tab === "settings" && <SiteSettingsManager />}
    </div>
  );
}


function UsersManager() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [verifFilter, setVerifFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    const emails = users.filter((u) => selected.has(u.id)).map((u) => u.email);
    if (!window.confirm(`Permanently delete ${ids.length} account(s)?\n\n${emails.slice(0, 10).join("\n")}${emails.length > 10 ? `\n…and ${emails.length - 10} more` : ""}\n\nAll their listings, products, RFQs and related rows are removed. This cannot be undone.`)) return;
    const typed = window.prompt(`Type DELETE (in capitals) to permanently remove ${ids.length} account(s):`);
    if ((typed || "").trim() !== "DELETE") { toast.info("Bulk deletion cancelled"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { user_ids: ids, confirm: "DELETE" });
      toast.success(`Deleted ${data.deleted_count} account(s)${data.skipped_count ? ` · ${data.skipped_count} skipped` : ""}`);
      if (data.skipped?.length) data.skipped.slice(0, 5).forEach((s) => toast.warning(`${s.email || s.id}: ${s.reason}`));
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally { setBulkBusy(false); }
  };

  const [loadError, setLoadError] = React.useState(null);
  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (roleFilter) params.set("role", roleFilter);
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      setUsers(data);
    } catch (e) {
      setUsers([]);
      setLoadError(describeLoadError(e, "users"));
      toast.error(e.response?.data?.detail || "Failed to load users");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Iter65 · client-side verification filter (Requested / Verified / Not verified)
  const filteredUsers = React.useMemo(() => {
    if (!verifFilter) return users;
    return users.filter((u) => {
      if (verifFilter === "requested") return !!u.verification_requested && !u.is_verified;
      if (verifFilter === "verified")  return !!u.is_verified;
      if (verifFilter === "unverified") return !u.is_verified && !u.verification_requested;
      return true;
    });
  }, [users, verifFilter]);

  const toggleVerified = async (u, next, extra = {}) => {
    const label = next ? "verified" : "unverified";
    const reg = extra.business_registration_number || u.detected_registration_number;
    const msg = next
      ? `Verify ${u.company || u.name || u.email}?${reg ? `\nRegistration number to save: ${reg}` : ""}\nThe green checkmark will immediately show on all their public listings.`
      : `Un-verify ${u.company || u.name || u.email}?\nThe green checkmark will be removed from all their public listings.`;
    if (!window.confirm(msg)) return;
    setSavingId(u.id);
    try {
      await api.post(`/admin/suppliers/${u.id}/verify`, {
        verified: next,
        business_registration_number:      extra.business_registration_number || u.detected_registration_number || undefined,
        business_registration_number_type: extra.business_registration_number_type || u.detected_registration_number_type || undefined,
      });
      toast.success(`${u.company || u.email} → ${label}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update verification");
    } finally { setSavingId(null); }
  };

  const [pasteModal, setPasteModal] = useState(null); // { user, body } | null

  const changeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    if (!window.confirm(`Change role of ${u.email} from '${u.role}' to '${newRole}'?`)) return;
    setSavingId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/role`, { role: newRole });
      toast.success(`Role updated: ${u.email} is now ${newRole}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update role");
    } finally { setSavingId(null); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}?\n\nThis will cascade-delete their listings, products, brand profiles, invited teammates, RFQs, payment history, subscribers & leads rows. This cannot be undone.`)) return;
    // Iter79 · Typed confirmation guard so a stray click can't nuke an account.
    const typed = window.prompt(`Type DELETE (in capitals) to permanently remove ${u.email}:`);
    if ((typed || "").trim() !== "DELETE") {
      toast.info("Deletion cancelled");
      return;
    }
    setSavingId(u.id);
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`, { data: { confirm: "DELETE" } });
      const cascade = Object.entries(data?.cascade || {}).map(([k, v]) => `${v} ${k}`).join(", ");
      toast.success(`Deleted ${u.email}${cascade ? ` — cascade: ${cascade}` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete user");
    } finally { setSavingId(null); }
  };

  const impersonate = async (u) => {
    if (!window.confirm(`View as ${u.email}?\n\nYou'll be signed in as this supplier for up to 2 hours. Every action is audit-logged and you can return to your admin session any time via the top banner.`)) return;
    setSavingId(u.id);
    try {
      await api.post(`/admin/impersonate/${u.id}`);
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success(`Now viewing as ${u.email}`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impersonation failed");
    } finally { setSavingId(null); }
  };

  const roleBadge = (r) => {
    const colours = {
      admin:      "bg-[#264839] text-white",
      advertiser: "bg-[#B35C4A] text-white",
      buyer:      "bg-[#F0EFEA] text-[#11211A]",
    };
    return <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 ${colours[r] || "bg-[#E8ECE9] text-[#4A5550]"}`}>{r}</span>;
  };

  return (
    <div data-testid="users-manager">
      <div className="flex items-end gap-3 mb-6 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <label className="block text-xs text-[#7D8C85] mb-1">Search</label>
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Email, name or company" className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="users-search" />
        </div>
        <div>
          <label className="block text-xs text-[#7D8C85] mb-1">Role filter</label>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="px-3 py-2 border border-[#C4CFC9] bg-white rounded-sm" data-testid="users-role-filter">
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="advertiser">Supplier</option>
            <option value="buyer">Buyer</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-[#7D8C85] mb-1">Verification</label>
          <select value={verifFilter} onChange={(e) => setVerifFilter(e.target.value)} className="px-3 py-2 border border-[#C4CFC9] bg-white rounded-sm" data-testid="users-verif-filter">
            <option value="">All</option>
            <option value="requested">🟡 Verification requested</option>
            <option value="verified">✅ Verified</option>
            <option value="unverified">⚪ Not verified</option>
          </select>
        </div>
        <button onClick={load} className="btn-primary" data-testid="users-refresh">Search</button>
        <div className="text-xs text-[#7D8C85] ml-auto">{filteredUsers.length} of {users.length} user{users.length === 1 ? "" : "s"}</div>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 px-4 py-2 bg-[#FBECE8] border border-[#B35C4A]/30 flex items-center gap-3 text-sm" data-testid="users-bulk-bar">
          <span className="text-[#11211A]" data-testid="users-bulk-count">{selected.size} selected</span>
          <button type="button" onClick={bulkDelete} disabled={bulkBusy} className="text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 bg-[#B35C4A] text-white hover:bg-[#994A3B] transition disabled:opacity-50" data-testid="users-bulk-delete">
            {bulkBusy ? "Deleting…" : "Delete selected"}
          </button>
          <button type="button" onClick={() => setSelected(new Set())} className="btn-ghost text-xs" data-testid="users-bulk-clear">Clear</button>
        </div>
      )}
      <LoadErrorBanner error={loadError} onRetry={load} testId="admin-users-error" />
      <div className="bg-white border border-[#E8ECE9] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F0EFEA] text-[#4A5550]">
            <tr className="text-left">
              <th className="px-3 py-3">
                {(() => { const eligible = filteredUsers.filter((u) => u.role !== "admin"); const all = eligible.length > 0 && eligible.every((u) => selected.has(u.id)); return (
                  <input type="checkbox" checked={all} onChange={() => setSelected(all ? new Set() : new Set(eligible.map((u) => u.id)))} title="Select all non-admin users shown" data-testid="users-select-all" />
                ); })()}
              </th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Name / Company</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Verification</th>
              <th className="px-4 py-3 font-medium">Change</th>
              <th className="px-4 py-3 font-medium">Trust badges</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#7D8C85]">Loading…</td></tr>}
            {!loading && loadError && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#B35C4A]" data-testid="admin-users-error-row">Users could not be loaded{loadError.status ? ` (HTTP ${loadError.status})` : ""}.</td></tr>}
            {!loading && !loadError && filteredUsers.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#7D8C85]">No users match</td></tr>}
            {filteredUsers.map((u) => (
              <tr key={u.id} className={`border-t border-[#E8ECE9] ${selected.has(u.id) ? "bg-[#FBECE8]/40" : ""}`} data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-3">
                  {u.role !== "admin" && <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleSelect(u.id)} data-testid={`user-select-${u.id}`} />}
                </td>
                <td className="px-4 py-3 text-[#11211A] break-all">{u.email}</td>
                <td className="px-4 py-3 text-[#4A5550]">
                  <div>{u.name || <span className="text-[#C4CFC9]">—</span>}</div>
                  {u.company && <div className="text-xs text-[#7D8C85]">{u.company}</div>}
                  {u.phone && (
                    <a
                      href={`tel:${u.phone}`}
                      className="text-[11px] font-semibold text-[#264839] mt-1 inline-flex items-center gap-1 hover:underline"
                      data-testid={`supplier-phone-${u.id}`}
                      title="Click to call"
                    >
                      📞 {u.phone}
                    </a>
                  )}
                  {u.onboarding_call_booked_at && (
                    <div className="text-[10px] text-[#264839] mt-1 inline-flex items-center gap-1" data-testid={`onboarding-call-badge-${u.id}`} title={`Onboarding call booked · ${(u.onboarding_call_booked_at + "").slice(0, 10)}${u.onboarding_call_booked_source ? " · " + u.onboarding_call_booked_source : ""}`}>
                      📞 Call booked
                    </div>
                  )}
                  {u.followup_link_clicked_at && !u.onboarding_call_followup_booked_at && (
                    <div
                      className="text-[10px] font-semibold text-[#B35C4A] mt-1 inline-flex items-center gap-1 bg-amber-50 border border-amber-300 px-1.5 py-0.5"
                      data-testid={`wants-followup-${u.id}`}
                      title={`Clicked the follow-up link · ${(u.followup_link_clicked_at + "").slice(0, 10)} · hasn't booked yet`}
                    >
                      🔔 Wants follow-up
                    </div>
                  )}
                  <ReminderLogChip user={u} />
                </td>
                <td className="px-4 py-3">{roleBadge(u.role || "buyer")}</td>
                <td className="px-4 py-3">
                  <VerificationCell
                    user={u}
                    onVerify={() => toggleVerified(u, true)}
                    onUnverify={() => toggleVerified(u, false)}
                    onPasteReply={() => setPasteModal({ user: u, body: "" })}
                    saving={savingId === u.id}
                  />
                </td>
                <td className="px-4 py-3">
                  <select
                    value={u.role || "buyer"}
                    onChange={(e) => changeRole(u, e.target.value)}
                    disabled={savingId === u.id}
                    className="px-2 py-1 border border-[#C4CFC9] bg-white rounded-sm text-xs disabled:opacity-50"
                    data-testid={`role-select-${u.id}`}
                  >
                    <option value="admin">Admin</option>
                    <option value="advertiser">Supplier</option>
                    <option value="buyer">Buyer</option>
                  </select>
                  {savingId === u.id && <span className="ml-2 text-xs text-[#7D8C85]">saving…</span>}
                </td>
                <td className="px-4 py-3">
                  <BadgeToggles user={u} onSaved={load} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 flex-wrap">
                    {u.role !== "admin" && (
                      <button
                        onClick={() => impersonate(u)}
                        disabled={savingId === u.id}
                        className="text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#264839] text-[#264839] bg-white hover:bg-[#264839] hover:text-white transition-colors disabled:opacity-50"
                        data-testid={`user-view-as-${u.id}`}
                        title={`Sign in as ${u.email} for up to 2 hours (audit-logged).`}
                      >
                        View as
                      </button>
                    )}
                    <button
                      onClick={() => deleteUser(u)}
                      disabled={savingId === u.id}
                      className="text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#B35C4A] text-[#B35C4A] bg-white hover:bg-[#B35C4A] hover:text-white transition-colors disabled:opacity-50"
                      data-testid={`user-delete-${u.id}`}
                      title="Permanently delete this user + cascade all their listings, products, brands, RFQs, subscribers & leads."
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-[#7D8C85] mt-4">
        Trust badges: toggle only after manually verifying each claim. You cannot demote your own admin account.
      </p>
      {pasteModal && (
        <PasteReplyModal
          user={pasteModal.user}
          onClose={() => setPasteModal(null)}
          onLogged={() => { setPasteModal(null); load(); }}
        />
      )}
    </div>
  );
}

const ADMIN_TRUST_BADGES = [
  { key: "details_confirmed",    label: "Details Confirmed" },
  { key: "responsive_supplier",  label: "Responsive Supplier" },
  { key: "fast_quote",           label: "Fast Quote" },
  { key: "shipping_clarity",     label: "Shipping Clarity" },
];

function BadgeToggles({ user, onSaved }) {
  const [saving, setSaving] = React.useState(null);
  const [savingLeader, setSavingLeader] = React.useState(false);
  const badges = user.badges || {};
  const toggle = async (key) => {
    setSaving(key);
    try {
      await api.post(`/admin/suppliers/${user.id}/badges`, { [key]: !badges[key] });
      toast.success(`${key} → ${!badges[key] ? "on" : "off"}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save badge");
    } finally { setSaving(null); }
  };
  const toggleLeader = async () => {
    setSavingLeader(true);
    try {
      await api.post(`/admin/suppliers/${user.id}/category-leader`, { is_category_leader: !user.is_category_leader });
      toast.success(`Category Leader → ${!user.is_category_leader ? "on" : "off"}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSavingLeader(false); }
  };
  return (
    <div className="flex flex-col gap-1.5" data-testid={`badges-${user.id}`}>
      <div className="flex flex-wrap gap-1">
        {ADMIN_TRUST_BADGES.map((b) => {
          const on = !!badges[b.key];
          return (
            <button
              key={b.key}
              onClick={() => toggle(b.key)}
              disabled={saving === b.key}
              className={`text-[10px] uppercase tracking-[0.1em] px-2 py-1 border transition ${on ? "bg-[#264839] text-white border-[#264839]" : "bg-white text-[#7D8C85] border-[#C4CFC9] hover:border-[#264839]"} disabled:opacity-50`}
              data-testid={`badge-${b.key}-${user.id}`}
              title={b.label}
            >
              {b.label}
            </button>
          );
        })}
      </div>
      <button
        onClick={toggleLeader}
        disabled={savingLeader}
        className={`text-[10px] uppercase tracking-[0.1em] px-2 py-1 border transition self-start ${user.is_category_leader ? "bg-[#B35C4A] text-white border-[#B35C4A]" : "bg-white text-[#B35C4A] border-[#B35C4A]/40 hover:border-[#B35C4A]"} disabled:opacity-50`}
        data-testid={`leader-${user.id}`}
        title="Category Leader — pins supplier to top of every category result with a 'Featured Leader' badge"
      >
        ★ Featured Leader
      </button>
    </div>
  );
}


// Iter65 · One-click verification cell for the Users table.
function VerificationCell({ user, onVerify, onUnverify, onPasteReply, saving }) {
  const requested   = !!user.verification_requested;
  const verified    = !!user.is_verified;
  const requestedAt = (user.verification_requested_at || "").slice(0, 10);
  const detected    = user.detected_registration_number;
  const detectedType = user.detected_registration_number_type;
  const detectedAt  = (user.detected_registration_number_at || "").slice(0, 10);
  const savedReg    = user.business_registration_number;
  const savedRegType = user.business_registration_number_type;

  if (verified) {
    return (
      <div className="flex flex-col gap-1 items-start" data-testid={`verif-cell-${user.id}`}>
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-semibold bg-emerald-600 text-white px-2 py-1" data-testid={`verified-pill-${user.id}`}>
          <BadgeCheck className="w-3 h-3" /> Verified
        </span>
        {savedReg && (
          <span className="text-[10px] text-[#7D8C85]" data-testid={`saved-reg-${user.id}`}>
            {savedRegType || "Reg"}: <code className="text-[#264839]">{savedReg}</code>
          </span>
        )}
        <button
          onClick={onUnverify}
          disabled={saving}
          className="text-[10px] text-[#B35C4A] hover:underline disabled:opacity-50"
          data-testid={`unverify-btn-${user.id}`}
        >
          Un-verify
        </button>
      </div>
    );
  }

  // Detected registration number = "Ready to Verify" — highest-priority actionable state.
  if (detected) {
    return (
      <div className="flex flex-col gap-1 items-start" data-testid={`verif-cell-${user.id}`}>
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-400 px-2 py-1" data-testid={`ready-to-verify-pill-${user.id}`} title={`Detected from supplier's email reply on ${detectedAt}`}>
          🟢 Ready to Verify
        </span>
        <span className="text-[10px] text-[#264839]" data-testid={`detected-reg-${user.id}`}>
          {detectedType}: <code className="bg-[#F0EFEA] px-1">{detected}</code>
        </span>
        <button
          onClick={onVerify}
          disabled={saving}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-semibold bg-emerald-600 text-white hover:bg-emerald-700 px-2 py-1 disabled:opacity-50 transition"
          data-testid={`approve-btn-${user.id}`}
        >
          <BadgeCheck className="w-3 h-3" /> Approve
        </button>
      </div>
    );
  }

  if (requested) {
    // Iter66 · countdown from verification_requested_at (fallback: created_at).
    const requestedIso = user.verification_requested_at || user.created_at || "";
    let daysWaiting = null;
    if (requestedIso) {
      const ms = Date.now() - new Date(requestedIso).getTime();
      if (!Number.isNaN(ms) && ms > 0) daysWaiting = Math.floor(ms / 86400000);
    }
    // Colour ladder: neutral <3d, amber 3-6d, red 7d+.
    let pillClass = "bg-amber-100 text-amber-800 border-amber-300";
    if (daysWaiting !== null && daysWaiting >= 7) pillClass = "bg-red-100 text-red-800 border-red-400";
    else if (daysWaiting !== null && daysWaiting >= 3) pillClass = "bg-amber-200 text-amber-900 border-amber-400";
    else pillClass = "bg-stone-100 text-stone-700 border-stone-300";

    const countdownText =
      daysWaiting === null
        ? "Awaiting reply"
        : daysWaiting === 0
          ? "Awaiting reply · today"
          : `Awaiting reply for ${daysWaiting} day${daysWaiting === 1 ? "" : "s"}`;

    return (
      <div className="flex flex-col gap-1 items-start" data-testid={`verif-cell-${user.id}`}>
        <span
          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-semibold border px-2 py-1 ${pillClass}`}
          data-testid={`verif-requested-pill-${user.id}`}
          data-days-waiting={daysWaiting ?? ""}
          title={requestedAt ? `Verification requested on ${requestedAt}` : "Verification requested"}
        >
          {countdownText}
        </span>
        <button
          onClick={onPasteReply}
          className="text-[10px] text-[#264839] hover:underline"
          data-testid={`paste-reply-btn-${user.id}`}
          title="Paste their email reply and we'll auto-detect the registration number"
        >
          Paste supplier reply
        </button>
        <button
          onClick={onVerify}
          disabled={saving}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-semibold bg-emerald-600 text-white hover:bg-emerald-700 px-2 py-1 disabled:opacity-50 transition"
          data-testid={`verify-btn-${user.id}`}
        >
          <BadgeCheck className="w-3 h-3" /> Mark verified
        </button>
      </div>
    );
  }

  if (user.role !== "advertiser") {
    return <span className="text-[10px] text-[#C4CFC9]" data-testid={`verif-cell-${user.id}`}>—</span>;
  }
  return (
    <div className="flex flex-col gap-1 items-start" data-testid={`verif-cell-${user.id}`}>
      <span className="text-[10px] text-[#7D8C85]">Not requested</span>
      <button
        onClick={onVerify}
        disabled={saving}
        className="text-[10px] text-[#264839] hover:underline disabled:opacity-50"
        data-testid={`verify-btn-${user.id}`}
      >
        Mark verified anyway
      </button>
    </div>
  );
}


// Iter65 · Admin fallback for when Resend inbound isn't wired: paste the supplier's
// email reply, we auto-detect the reg-number and stamp it on the user record.
function PasteReplyModal({ user, onClose, onLogged }) {
  const [body, setBody] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const submit = async () => {
    if (!body.trim()) { toast.error("Paste the supplier's reply first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/suppliers/${user.id}/verification-reply`, { body });
      setResult(data);
      if (data.detected) {
        toast.success(`${data.detected.type} detected: ${data.detected.formatted}`);
        // Give the operator a beat to see the detection before we close.
        setTimeout(onLogged, 1200);
      } else {
        toast.warning("No registration number found in that text. Try again or verify manually.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log reply");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center px-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="paste-reply-modal">
      <div className="bg-white max-w-lg w-full p-6 shadow-2xl border-l-4 border-[#264839]">
        <div className="tracking-overline text-[10px] text-[#264839] mb-1">Log supplier's verification reply</div>
        <h3 className="font-serif text-xl text-[#11211A] mb-1">{user.company || user.name || user.email}</h3>
        <div className="text-xs text-[#7D8C85] mb-4">{user.email}</div>
        <label className="block text-xs uppercase tracking-widest text-[#264839] mb-1 font-semibold">Paste the supplier's email reply</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          placeholder={"E.g. 'Hi, my ABN is 51 824 753 556. Thanks!'"}
          className="w-full px-3 py-2 border border-[#C4CFC9] text-sm bg-white font-mono"
          data-testid="paste-reply-textarea"
        />
        {result?.detected && (
          <div className="mt-3 p-3 bg-emerald-50 border border-emerald-300 text-sm" data-testid="paste-reply-detected">
            <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">Detected</div>
            <div className="text-[#264839] mt-1"><strong>{result.detected.type}</strong>: <code>{result.detected.formatted}</code></div>
            <div className="text-[11px] text-[#7D8C85] mt-1">Closing… the row will refresh with a 🟢 Ready to Verify pill.</div>
          </div>
        )}
        <div className="mt-4 flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-xs" data-testid="paste-reply-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="bg-[#264839] text-white px-4 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#1B3529] transition disabled:opacity-50" data-testid="paste-reply-submit">
            {busy ? "Scanning…" : "Detect number"}
          </button>
        </div>
      </div>
    </div>
  );
}



function FoundingSuppliersManager() {
  const [subTab, setSubTab] = React.useState("suppliers"); // "suppliers" | "nudges" | "add"
  const [rows, setRows] = React.useState([]);
  const [nudges, setNudges] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [nudgesLoading, setNudgesLoading] = React.useState(false);
  const [slugs, setSlugs] = React.useState([]);
  const [defaults, setDefaults] = React.useState([]);
  const [slugInput, setSlugInput] = React.useState("");
  const [savingSlugs, setSavingSlugs] = React.useState(false);
  // Iter57 filters
  const [tierFilter, setTierFilter] = React.useState("");
  const [progressFilter, setProgressFilter] = React.useState("");
  // Iter69 · live slot counter
  const [slots, setSlots] = React.useState(null);

  const loadSlots = async () => {
    try {
      const { data } = await api.get("/admin/founding-slots");
      setSlots(data);
    } catch (_) { /* noop */ }
  };
  React.useEffect(() => {
    loadSlots();
    const iv = setInterval(loadSlots, 30000);
    return () => clearInterval(iv);
  }, []);

  const [loadError, setLoadError] = React.useState(null);
  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [rep, sl] = await Promise.all([
        api.get("/admin/founding-suppliers"),
        api.get("/admin/founding-featured-slugs"),
      ]);
      setRows(rep.data.items || []);
      setSlugs(sl.data.slugs || []);
      setDefaults(sl.data.defaults || []);
    } catch (e) {
      setRows([]);
      setLoadError(describeLoadError(e, "founding suppliers"));
      toast.error(e.response?.data?.detail || "Failed to load founding suppliers");
    } finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, []);

  const loadNudges = async () => {
    setNudgesLoading(true);
    try {
      const { data } = await api.get("/admin/onboarding/nudge-history");
      setNudges(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load nudge history");
    } finally { setNudgesLoading(false); }
  };
  React.useEffect(() => { if (subTab === "nudges") loadNudges(); }, [subTab]);

  const downloadCsv = () => {
    // Iter57: client-side CSV of the currently-filtered rows so the export reflects the visible table.
    const cols = ["email","name","company","tier","founding_tier_at_signup","founding_started_at",
                  "founding_price","founding_price_locked_until","founding_featured_until",
                  "onboarding_pct","onboarding_steps_done","subscription_status"];
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [cols.join(",")];
    for (const r of filteredRows) lines.push(cols.map((c) => esc(r[c])).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url; a.download = `founding-suppliers-${stamp}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const addSlug = () => {
    const s = slugInput.trim().toLowerCase();
    if (!s) return;
    if (slugs.includes(s)) { toast.info("Already in the list"); setSlugInput(""); return; }
    setSlugs([...slugs, s]);
    setSlugInput("");
  };
  const removeSlug = (s) => setSlugs(slugs.filter((x) => x !== s));
  const resetToDefaults = () => setSlugs([...defaults]);
  const saveSlugs = async () => {
    setSavingSlugs(true);
    try {
      const { data } = await api.put("/admin/founding-featured-slugs", { slugs });
      setSlugs(data.slugs);
      toast.success(`Saved ${data.count} slug${data.count === 1 ? "" : "s"} — new Founding Suppliers in these categories get 50-day pin.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save slugs");
    } finally { setSavingSlugs(false); }
  };

  const fmtDate = (iso) => iso ? iso.split("T")[0] : "—";

  // Iter73 · Per-row action handlers (mirror UsersManager toggles)
  const [rowSaving, setRowSaving] = React.useState({}); // { [userId]: "founding" | "leader" | "claim" }
  // Iter76 · Bulk-select state
  const [selected, setSelected] = React.useState(() => new Set());
  const [bulkDate, setBulkDate] = React.useState("");
  const [bulkSaving, setBulkSaving] = React.useState(false);
  const toggleSelected = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const toggleLeaderRow = async (r) => {
    setRowSaving((s) => ({ ...s, [r.id]: "leader" }));
    try {
      const next = !r.is_category_leader;
      await api.post(`/admin/suppliers/${r.id}/category-leader`, { is_category_leader: next });
      setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, is_category_leader: next } : x));
      toast.success(`Featured Leader → ${next ? "on" : "off"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to toggle Featured Leader");
    } finally {
      setRowSaving((s) => { const c = { ...s }; delete c[r.id]; return c; });
    }
  };
  const markFoundingRow = async (r) => {
    if (r.founding_supplier) return;
    if (!window.confirm(
      `Mark ${r.company || r.name || r.email} as Founding Supplier?\n\n` +
      `• Locks their current tier price for 12 months\n` +
      `• Adds a 50-day featured pin\n` +
      `• Cascades the Founding badge to every one of their listings immediately`
    )) return;
    setRowSaving((s) => ({ ...s, [r.id]: "founding" }));
    try {
      const { data } = await api.post(`/admin/suppliers/${r.id}/founding`, { tier: r.tier || "starter" });
      if (data.already_tagged) toast.info(`${r.company || r.email} was already Founding`);
      else toast.success(`${r.company || r.email} tagged as Founding · price locked $${data.founding_price?.toFixed(2)}`);
      await load();
      loadSlots();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to tag as Founding");
    } finally {
      setRowSaving((s) => { const c = { ...s }; delete c[r.id]; return c; });
    }
  };
  // Iter76 · Send claim link to an unclaimed supplier
  const sendClaimLink = async (r) => {
    if (!r.is_unclaimed) return;
    if (!window.confirm(
      `Email a claim link to ${r.email}?\n\n` +
      `• 7-day magic link — they set their password + activate the listing\n` +
      `• Any prior reset/claim tokens for this account are invalidated`
    )) return;
    setRowSaving((s) => ({ ...s, [r.id]: "claim" }));
    try {
      const { data } = await api.post(`/admin/suppliers/${r.id}/send-claim-link`);
      toast.success(`Claim link sent to ${data.email}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send claim link");
    } finally {
      setRowSaving((s) => { const c = { ...s }; delete c[r.id]; return c; });
    }
  };
  // Iter76 · Bulk mass-update featured_until for the current selection
  const applyBulkFeaturedUntil = async () => {
    if (!bulkDate) return toast.error("Pick a Featured-until date first");
    if (selected.size === 0) return toast.error("Select at least one row");
    if (!window.confirm(
      `Update Featured-until to ${bulkDate} for ${selected.size} supplier${selected.size === 1 ? "" : "s"}?\n\n` +
      `• Cascades the same date to every listing they own`
    )) return;
    setBulkSaving(true);
    try {
      const { data } = await api.post("/admin/founding-suppliers/bulk-featured-until", {
        user_ids: Array.from(selected),
        featured_until: bulkDate,
      });
      toast.success(`Updated ${data.users_updated} supplier${data.users_updated === 1 ? "" : "s"} · ${data.listings_updated} listing${data.listings_updated === 1 ? "" : "s"}`);
      clearSelection();
      setBulkDate("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update Featured-until");
    } finally {
      setBulkSaving(false);
    }
  };
  // Iter77 · Bulk send claim links to every unclaimed row in the selection
  const [bulkClaiming, setBulkClaiming] = React.useState(false);
  const sendBulkClaimLinks = async () => {
    if (selected.size === 0) return toast.error("Select at least one row");
    const unclaimedCount = filteredRows.filter((r) => selected.has(r.id) && r.is_unclaimed).length;
    if (unclaimedCount === 0) {
      return toast.info("Every selected supplier has already claimed their account.");
    }
    if (!window.confirm(
      `Email a 7-day claim link to ${unclaimedCount} unclaimed supplier${unclaimedCount === 1 ? "" : "s"}?\n\n` +
      `• Any already-claimed accounts in the selection will be skipped\n` +
      `• Any outstanding reset/claim tokens are invalidated`
    )) return;
    setBulkClaiming(true);
    try {
      const { data } = await api.post("/admin/founding-suppliers/bulk-send-claim-links", {
        user_ids: Array.from(selected),
      });
      toast.success(`Sent ${data.sent} claim link${data.sent === 1 ? "" : "s"} · skipped ${data.skipped}${data.failed ? ` · failed ${data.failed}` : ""}`);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send claim links");
    } finally {
      setBulkClaiming(false);
    }
  };

  // Iter57: client-side filters
  const filteredRows = rows.filter((r) => {
    if (tierFilter && r.tier !== tierFilter) return false;
    if (progressFilter === "under60" && r.onboarding_pct > 50) return false;
    if (progressFilter === "60to99" && (r.onboarding_pct < 60 || r.onboarding_pct >= 100)) return false;
    if (progressFilter === "complete" && r.onboarding_pct < 100) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="founding-suppliers-manager">
      <FoundingOnboardingCheck defaultQuery="Setuply" />
      <SpotlightClicksTable />
      <FeaturedReportPanel />
      {/* Iter69 · Live slot counter banner */}
      {slots && (
        <div
          className={`border-l-4 p-4 flex items-center gap-4 ${slots.remaining <= 5 ? "border-[#B35C4A] bg-[#FBEEE9]" : slots.remaining <= 15 ? "border-amber-500 bg-amber-50" : "border-[#264839] bg-[#F0EFEA]"}`}
          data-testid="founding-slots-banner"
        >
          <div className="flex-1">
            <div className="tracking-overline text-[10px] text-[#264839]">Founding cohort</div>
            <div className="font-serif text-2xl text-[#11211A] mt-0.5" data-testid="founding-slots-summary">
              <span className="tabular-nums" data-testid="founding-slots-remaining">{slots.remaining}</span> of {slots.total} slots remaining
            </div>
            <div className="text-[11px] text-[#7D8C85] mt-1">
              {slots.taken} suppliers tagged · Deadline {slots.deadline_open ? "open" : "closed"}
            </div>
          </div>
          <div className="w-40 h-2 bg-white/60 overflow-hidden">
            <div
              className={`h-full ${slots.remaining <= 5 ? "bg-[#B35C4A]" : slots.remaining <= 15 ? "bg-amber-500" : "bg-[#264839]"}`}
              style={{ width: `${Math.min(100, (slots.taken / (slots.total || 1)) * 100)}%` }}
            />
          </div>
          <button onClick={loadSlots} className="text-[10px] uppercase tracking-widest text-[#264839] hover:underline" data-testid="founding-slots-refresh">Refresh</button>
        </div>
      )}

      {/* Iter58 sub-tabs */}
      <div className="flex border-b border-[#E8ECE9]">
        <button
          onClick={() => setSubTab("suppliers")}
          className={`px-4 py-2 text-sm border-b-2 -mb-px ${subTab === "suppliers" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`}
          data-testid="founding-subtab-suppliers"
        >
          Suppliers ({rows.length})
        </button>
        <button
          onClick={() => setSubTab("add")}
          className={`px-4 py-2 text-sm border-b-2 -mb-px ${subTab === "add" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`}
          data-testid="founding-subtab-add"
        >
          + Mark supplier as Founding
        </button>
        <button
          onClick={() => setSubTab("nudges")}
          className={`px-4 py-2 text-sm border-b-2 -mb-px ${subTab === "nudges" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`}
          data-testid="founding-subtab-nudges"
        >
          Nudge History
        </button>
      </div>

      {subTab === "add" && (
        <MarkFoundingPanel onTagged={() => { load(); loadSlots(); }} />
      )}

      {subTab === "nudges" && (
        <div className="bg-white border border-[#E8ECE9] p-6" data-testid="nudge-history-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="font-serif text-xl text-[#11211A]">Stalled-onboarding nudge history</div>
              <p className="text-xs text-[#7D8C85] mt-1">Every automated onboarding email dispatched — audit trail for outreach without opening Resend.</p>
            </div>
            <button onClick={loadNudges} disabled={nudgesLoading} className="btn-ghost text-xs disabled:opacity-50" data-testid="nudge-history-refresh">
              {nudgesLoading ? "Loading…" : "Refresh"}
            </button>
          </div>
          {nudgesLoading && <div className="py-6 text-center text-[#7D8C85]">Loading…</div>}
          {!nudgesLoading && nudges.length === 0 && <div className="bg-[#F0EFEA] p-8 text-center text-[#7D8C85]">No nudges have been sent yet.</div>}
          {!nudgesLoading && nudges.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="nudge-history-table">
                <thead className="text-[#7D8C85] uppercase tracking-[0.15em] text-[10px]">
                  <tr className="border-b border-[#E8ECE9]">
                    <th className="text-left px-3 py-2">Recipient</th>
                    <th className="text-left px-3 py-2">Tier</th>
                    <th className="text-left px-3 py-2">Kind</th>
                    <th className="text-left px-3 py-2">Sent</th>
                    <th className="text-right px-3 py-2">Onboarding % at send</th>
                  </tr>
                </thead>
                <tbody>
                  {nudges.map((n) => (
                    <tr key={n.id} className="border-b border-[#E8ECE9] hover:bg-[#F5F7F5]" data-testid={`nudge-row-${n.id}`}>
                      <td className="px-3 py-2 text-[#11211A] break-all">
                        <div>{n.email}</div>
                        {n.name && <div className="text-[10px] text-[#7D8C85]">{n.name}</div>}
                      </td>
                      <td className="px-3 py-2"><span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[#F0EFEA] text-[#264839]">{n.tier || "—"}</span></td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${
                          n.kind === "second_chance" ? "bg-[#FFFBEB] text-[#B8860B]" : "bg-[#FFF4EF] text-[#B35C4A]"
                        }`}>
                          {n.kind === "second_chance" ? "Second chance" : "First nudge"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[#4A5550] tabular-nums">{n.sent_at ? new Date(n.sent_at).toLocaleString() : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#11211A]">{typeof n.pct_at_send === "number" ? `${n.pct_at_send}%` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {subTab === "suppliers" && <>
      {/* Category slug editor */}
      <div className="bg-white border border-[#E8ECE9] p-6" data-testid="founding-slug-editor">
        <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
          <div>
            <div className="font-serif text-xl text-[#11211A]">50-day featured category slugs</div>
            <p className="text-xs text-[#7D8C85] mt-1 max-w-xl leading-relaxed">
              New Founding Suppliers whose listings match any of these category slugs get automatic 50-day pinned placement.
              Add slugs for new category rollouts (e.g. <code>furniture</code>, <code>amenities</code>) — no code deploy needed.
            </p>
          </div>
          <button onClick={resetToDefaults} className="text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#7D8C85] text-[#7D8C85] hover:bg-[#F0EFEA] transition" data-testid="founding-slugs-reset">
            Reset to defaults
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3" data-testid="founding-slugs-list">
          {slugs.map((s) => (
            <span key={s} className="inline-flex items-center gap-1.5 bg-[#F0EFEA] border border-[#264839]/20 px-2.5 py-1 text-xs font-mono text-[#264839]" data-testid={`founding-slug-${s}`}>
              {s}
              <button onClick={() => removeSlug(s)} className="text-[#B35C4A] hover:text-[#9E4E3E] font-bold" aria-label={`Remove ${s}`}>×</button>
            </span>
          ))}
          {slugs.length === 0 && <span className="text-xs text-[#7D8C85] italic">No slugs — no listings will get the 50-day pin.</span>}
        </div>
        <div className="flex gap-2 mt-4">
          <input
            type="text"
            value={slugInput}
            onChange={(e) => setSlugInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSlug(); } }}
            placeholder="e.g. furniture"
            className="flex-1 max-w-xs px-3 py-2 border border-[#C4CFC9] text-sm rounded-sm bg-white"
            data-testid="founding-slug-input"
          />
          <button onClick={addSlug} className="btn-secondary text-xs" data-testid="founding-slug-add">Add slug</button>
          <button onClick={saveSlugs} disabled={savingSlugs} className="btn-primary text-xs disabled:opacity-50" data-testid="founding-slugs-save">
            {savingSlugs ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* Founding suppliers table */}
      <div className="bg-white border border-[#E8ECE9] p-6">
        {/* Iter76 · Sticky bulk-action bar shown when any row is selected */}
        {selected.size > 0 && (
          <div
            className="sticky top-2 z-10 mb-4 bg-[#264839] text-white p-3 flex items-center gap-3 flex-wrap shadow-lg"
            data-testid="founding-bulk-bar"
          >
            <span className="text-xs uppercase tracking-widest" data-testid="founding-bulk-count">
              {selected.size} selected
            </span>
            <span className="text-xs text-white/70">Set Featured until:</span>
            <input
              type="date"
              value={bulkDate}
              onChange={(e) => setBulkDate(e.target.value)}
              min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
              className="px-2 py-1 text-xs text-[#11211A] bg-white border border-white/40"
              data-testid="founding-bulk-date"
            />
            <button
              onClick={applyBulkFeaturedUntil}
              disabled={bulkSaving || !bulkDate}
              className="text-[10px] uppercase tracking-widest font-semibold bg-[#B35C4A] hover:bg-[#9E4E3E] px-3 py-1.5 transition disabled:opacity-50"
              data-testid="founding-bulk-apply"
            >
              {bulkSaving ? "Applying…" : "Apply to selected"}
            </button>
            <button
              onClick={sendBulkClaimLinks}
              disabled={bulkClaiming}
              className="text-[10px] uppercase tracking-widest font-semibold bg-white text-[#264839] hover:bg-[#F0EFEA] px-3 py-1.5 transition disabled:opacity-50"
              data-testid="founding-bulk-send-claim-links"
              title="Email a 7-day claim link to every unclaimed row in the selection. Already-claimed rows are skipped."
            >
              {bulkClaiming ? "Sending…" : "✉ Send claim links"}
            </button>
            <button
              onClick={clearSelection}
              className="text-[10px] uppercase tracking-widest text-white/70 hover:text-white ml-auto"
              data-testid="founding-bulk-clear"
            >
              Clear selection
            </button>
          </div>
        )}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="font-serif text-xl text-[#11211A]">Founding Suppliers ({filteredRows.length}{filteredRows.length !== rows.length ? ` / ${rows.length}` : ""})</div>
            <p className="text-xs text-[#7D8C85] mt-1">Every user tagged during the launch cohort — signup date, locked price, pin expiry, onboarding %.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="text-xs border border-[#C4CFC9] px-2 py-1.5 rounded-sm bg-white"
              data-testid="founding-filter-tier"
            >
              <option value="">All tiers</option>
              <option value="starter">Starter</option>
              <option value="growth">Growth</option>
              <option value="leader">Category Leader</option>
              <option value="enterprise">Enterprise</option>
            </select>
            <select
              value={progressFilter}
              onChange={(e) => setProgressFilter(e.target.value)}
              className="text-xs border border-[#C4CFC9] px-2 py-1.5 rounded-sm bg-white"
              data-testid="founding-filter-progress"
            >
              <option value="">All onboarding %</option>
              <option value="under60">50% or below (stalled)</option>
              <option value="60to99">60–99%</option>
              <option value="complete">Complete (100%)</option>
            </select>
            <button onClick={downloadCsv} disabled={!filteredRows.length} className="btn-ghost disabled:opacity-50" data-testid="founding-suppliers-export-csv">
              Export CSV
            </button>
            <SendFoundingWelcomeButton onDone={load} />
          </div>
        </div>
        {loading && <div className="py-6 text-center text-[#7D8C85]">Loading…</div>}
        <LoadErrorBanner error={loadError} onRetry={load} testId="admin-founding-error" />
        {!loading && !loadError && filteredRows.length === 0 && rows.length === 0 && <div className="bg-[#F0EFEA] p-8 text-center text-[#7D8C85]">No Founding Suppliers yet.</div>}
        {!loading && filteredRows.length === 0 && rows.length > 0 && <div className="bg-[#F0EFEA] p-8 text-center text-[#7D8C85]">No suppliers match the current filters.</div>}
        {!loading && filteredRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="founding-suppliers-table">
              <thead className="text-[#7D8C85] uppercase tracking-[0.15em] text-[10px]">
                <tr className="border-b border-[#E8ECE9]">
                  <th className="text-left px-2 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={filteredRows.length > 0 && filteredRows.every((r) => selected.has(r.id))}
                      onChange={(e) => {
                        if (e.target.checked) setSelected(new Set(filteredRows.map((r) => r.id)));
                        else clearSelection();
                      }}
                      data-testid="founding-select-all"
                      aria-label="Select all rows"
                    />
                  </th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Company</th>
                  <th className="text-left px-3 py-2">Tier</th>
                  <th className="text-left px-3 py-2">Signup</th>
                  <th className="text-right px-3 py-2">Locked $</th>
                  <th className="text-left px-3 py-2">Price locked until</th>
                  <th className="text-left px-3 py-2">Featured until</th>
                  <th className="text-left px-3 py-2">Signup complete</th>
                  <th className="text-right px-3 py-2">Onboarding</th>
                  <th className="text-left px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr key={r.id} className={`border-b border-[#E8ECE9] hover:bg-[#F5F7F5] ${selected.has(r.id) ? "bg-[#F0EFEA]/70" : ""}`} data-testid={`founding-row-${r.id}`}>
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => toggleSelected(r.id)}
                        data-testid={`founding-select-${r.id}`}
                        aria-label={`Select ${r.email}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-[#11211A] break-all">
                      {r.email}
                      {r.is_unclaimed && (
                        <span className="ml-2 text-[9px] uppercase tracking-widest bg-amber-100 text-amber-800 px-1 py-0.5" data-testid={`unclaimed-pill-${r.id}`}>
                          Unclaimed
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[#4A5550]">{r.company || r.name || "—"}</td>
                    <td className="px-3 py-2"><span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[#F0EFEA] text-[#264839]">{r.tier}</span></td>
                    <td className="px-3 py-2 text-[#4A5550] tabular-nums">{fmtDate(r.founding_started_at)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#11211A]">{r.founding_price ? `$${r.founding_price}` : "—"}</td>
                    <td className="px-3 py-2 text-[#4A5550] tabular-nums">{fmtDate(r.founding_price_locked_until)}</td>
                    <td className="px-3 py-2 text-[#4A5550] tabular-nums">{fmtDate(r.founding_featured_until)}</td>
                    <td className="px-3 py-2" data-testid={`signup-status-${r.id}`}>
                      {r.is_unclaimed ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-amber-100 text-amber-800 self-start">
                            ⏳ Pending
                          </span>
                          <span className="text-[10px] text-[#7D8C85]">
                            {r.claim_link_sent_at
                              ? `Link sent ${fmtDate(r.claim_link_sent_at)}`
                              : "No claim link sent"}
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-emerald-100 text-emerald-800 self-start">
                            ✓ Signed up
                          </span>
                          {r.password_changed_at && (
                            <span className="text-[10px] text-[#7D8C85]">
                              {fmtDate(r.password_changed_at)}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-[#F0EFEA] overflow-hidden">
                          <div className="h-full bg-[#264839]" style={{ width: `${r.onboarding_pct}%` }} />
                        </div>
                        <span className="tabular-nums text-[#11211A] text-xs w-9">{r.onboarding_pct}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1.5 items-start" data-testid={`founding-actions-${r.id}`}>
                        {r.founding_supplier ? (
                          <span
                            className="text-[10px] uppercase tracking-[0.1em] px-2 py-1 bg-[#264839] text-white"
                            data-testid={`founding-tagged-${r.id}`}
                            title="Already tagged as Founding Supplier"
                          >
                            ✓ Founding
                          </span>
                        ) : (
                          <button
                            onClick={() => markFoundingRow(r)}
                            disabled={rowSaving[r.id] === "founding"}
                            className="text-[10px] uppercase tracking-[0.1em] px-2 py-1 border bg-white text-[#264839] border-[#264839]/40 hover:border-[#264839] transition disabled:opacity-50"
                            data-testid={`mark-founding-${r.id}`}
                            title="Mark as Founding Supplier — locks price, adds 50-day pin, cascades to listings"
                          >
                            {rowSaving[r.id] === "founding" ? "Marking…" : "Mark as Founding"}
                          </button>
                        )}
                        <button
                          onClick={() => toggleLeaderRow(r)}
                          disabled={rowSaving[r.id] === "leader"}
                          className={`text-[10px] uppercase tracking-[0.1em] px-2 py-1 border transition disabled:opacity-50 ${
                            r.is_category_leader
                              ? "bg-[#B35C4A] text-white border-[#B35C4A]"
                              : "bg-white text-[#B35C4A] border-[#B35C4A]/40 hover:border-[#B35C4A]"
                          }`}
                          data-testid={`toggle-leader-${r.id}`}
                          title="Category Leader — pins supplier to top of every category result with a 'Featured Leader' badge"
                        >
                          {rowSaving[r.id] === "leader" ? "…" : "★ Featured Leader"}
                        </button>
                        {r.is_unclaimed && (
                          <button
                            onClick={() => sendClaimLink(r)}
                            disabled={rowSaving[r.id] === "claim"}
                            className="text-[10px] uppercase tracking-[0.1em] px-2 py-1 border bg-white text-[#264839] border-[#264839]/40 hover:border-[#264839] transition disabled:opacity-50"
                            data-testid={`send-claim-link-${r.id}`}
                            title={r.claim_link_sent_at ? `Last claim link sent ${new Date(r.claim_link_sent_at).toLocaleString()}` : "Email a 7-day magic link so the supplier can claim & activate their listing"}
                          >
                            {rowSaving[r.id] === "claim" ? "Sending…" : (r.claim_link_sent_at ? "Resend claim link" : "Send claim link")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>}
    </div>
  );
}



// Iter75 · One-shot admin trigger to blast the Founding welcome email to every
// tagged supplier who hasn't yet received it. Backend guards against re-sends
// via `founding_welcome_sent_at`, so clicking twice is safe.
function SendFoundingWelcomeButton({ onDone }) {
  const [sending, setSending] = React.useState(false);
  const send = async () => {
    if (!window.confirm(
      "Send the Founding Welcome email to every Founding Supplier who hasn't received it yet?\n\n" +
      "• Each recipient gets their locked price + featured-until dates\n" +
      "• Idempotent — anyone already emailed will be skipped"
    )) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/founding-suppliers/send-welcome-emails");
      toast.success(`Welcome blast complete · sent ${data.sent} · skipped ${data.skipped}${data.failed ? ` · failed ${data.failed}` : ""}`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send welcome emails");
    } finally { setSending(false); }
  };
  return (
    <button
      onClick={send}
      disabled={sending}
      className="text-[10px] uppercase tracking-widest font-semibold bg-[#264839] text-white hover:bg-[#1a3428] px-3 py-2 transition disabled:opacity-50"
      data-testid="send-founding-welcome-btn"
      title="Send the Founding Welcome email to every tagged supplier who hasn't received it yet"
    >
      {sending ? "Sending…" : "✉ Send welcome emails"}
    </button>
  );
}


// Iter69 · Small in-tab panel to tag a supplier as Founding by picking them
// from the advertiser list. Bypasses the Stripe/deadline/slots gates that
// govern the automatic tagger — this is an admin-only override.
function MarkFoundingPanel({ onTagged }) {
  const [q, setQ] = React.useState("");
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [tagging, setTagging] = React.useState(null); // user_id being tagged
  // Iter75 · optional admin override for the featured-until pin date.
  // Blank = default 50 days from now (backend fallback).
  const [featuredUntil, setFeaturedUntil] = React.useState("");

  const search = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ role: "advertiser" });
      if (q) params.set("q", q);
      const { data } = await api.get(`/admin/users?${params}`);
      // Only show suppliers who are NOT already tagged.
      setUsers((data || []).filter((u) => !u.founding_supplier));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to search suppliers");
    } finally { setLoading(false); }
  };
  React.useEffect(() => { search(); /* eslint-disable-next-line */ }, []);

  const markFounding = async (u) => {
    const featuredNote = featuredUntil
      ? `• Featured pin runs until ${featuredUntil}`
      : `• Adds a 50-day featured pin`;
    if (!window.confirm(
      `Mark ${u.company || u.name || u.email} as Founding Supplier?\n\n` +
      `• Locks their current tier price for 12 months\n` +
      `${featuredNote}\n` +
      `• Cascades the Founding badge to every one of their listings immediately`
    )) return;
    setTagging(u.id);
    try {
      const body = { tier: u.tier || "starter" };
      if (featuredUntil) body.featured_until = featuredUntil;
      const { data } = await api.post(`/admin/suppliers/${u.id}/founding`, body);
      if (data.already_tagged) {
        toast.info(`${u.company || u.email} was already Founding`);
      } else {
        toast.success(`${u.company || u.email} tagged as Founding · price locked $${data.founding_price?.toFixed(2)}`);
      }
      onTagged?.();
      search();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to tag as Founding");
    } finally { setTagging(null); }
  };

  return (
    <div className="bg-white border border-[#E8ECE9] p-6" data-testid="mark-founding-panel">
      <div className="mb-4">
        <div className="font-serif text-xl text-[#11211A]">Mark a supplier as Founding</div>
        <p className="text-xs text-[#7D8C85] mt-1">
          Bypasses the automatic gates (deadline, slot count). Use for edge-case retroactive tagging — e.g. a supplier who paid outside Stripe or was missed by the initial cohort scan.
        </p>
      </div>
      <div className="flex gap-2 items-end mb-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs text-[#7D8C85] mb-1">Search advertisers</label>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") search(); }}
            placeholder="Email, name or company"
            className="w-full px-3 py-2 border border-[#C4CFC9] text-sm bg-white"
            data-testid="mark-founding-search"
          />
        </div>
        <div>
          <label className="block text-xs text-[#7D8C85] mb-1" title="Leave blank for the default 50-day pin from today">
            Featured until <span className="text-[10px] text-[#7D8C85]">(optional override)</span>
          </label>
          <input
            type="date"
            value={featuredUntil}
            onChange={(e) => setFeaturedUntil(e.target.value)}
            min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
            className="px-3 py-2 border border-[#C4CFC9] text-sm bg-white"
            data-testid="mark-founding-featured-until"
          />
        </div>
        <button onClick={search} disabled={loading} className="btn-primary disabled:opacity-50" data-testid="mark-founding-search-btn">
          {loading ? "…" : "Search"}
        </button>
      </div>

      {loading && <div className="text-sm text-[#7D8C85]">Loading…</div>}
      {!loading && users.length === 0 && (
        <div className="bg-[#F0EFEA] p-6 text-center text-sm text-[#7D8C85]" data-testid="mark-founding-empty">
          No non-Founding advertisers match. Every advertiser you can see is already tagged.
        </div>
      )}
      {!loading && users.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-[#7D8C85] uppercase tracking-[0.15em] text-[10px] border-b border-[#E8ECE9]">
              <tr className="text-left">
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Company / Name</th>
                <th className="px-3 py-2">Tier</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {users.slice(0, 50).map((u) => (
                <tr key={u.id} className="border-b border-[#F0EFEA]" data-testid={`mark-founding-row-${u.id}`}>
                  <td className="px-3 py-2 text-[#11211A] break-all">{u.email}</td>
                  <td className="px-3 py-2 text-[#4A5550]">
                    <div>{u.company || u.name || "—"}</div>
                    {u.company && u.name && <div className="text-[11px] text-[#7D8C85]">{u.name}</div>}
                  </td>
                  <td className="px-3 py-2 text-[#4A5550]">{u.tier || "free"}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => markFounding(u)}
                      disabled={tagging === u.id}
                      className="text-[10px] uppercase tracking-widest font-semibold bg-[#B35C4A] text-white hover:bg-[#9E4E3E] px-3 py-1.5 transition disabled:opacity-50"
                      data-testid={`mark-founding-btn-${u.id}`}
                    >
                      {tagging === u.id ? "Tagging…" : "★ Mark as Founding"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length > 50 && (
            <div className="text-[11px] text-[#7D8C85] mt-3">Showing first 50 of {users.length}. Refine the search to narrow down.</div>
          )}
        </div>
      )}
    </div>
  );
}


// Iter70 · Compact chip showing the most recent reminder that fired for a
// supplier (SLA reminder + booking reminder + booking follow-up). Green = ok,
// red = last send failed, grey = never sent.
function ReminderLogChip({ user }) {
  // Order matters: most-recent-first list.
  const events = [
    { key: "verification_sla_reminder_sent_at", ok_key: "verification_sla_reminder_ok", label: "SLA nudge" },
    { key: "onboarding_call_reminder_sent_at",  ok_key: "onboarding_call_reminder_ok",  label: "24h reminder" },
    { key: "onboarding_call_followup_sent_at",  ok_key: "onboarding_call_followup_ok",  label: "Follow-up" },
    { key: "second_chance_sent_at",             ok_key: null,                            label: "2nd-chance nudge" },
  ];
  let latest = null;
  for (const ev of events) {
    const ts = user[ev.key];
    if (ts && (!latest || ts > latest.ts)) {
      latest = { ts, ...ev, ok: ev.ok_key ? user[ev.ok_key] : true };
    }
  }
  if (!latest) return null;
  const cls = latest.ok ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-red-700 bg-red-50 border-red-200";
  const short = (latest.ts + "").slice(0, 10);
  return (
    <div className={`text-[10px] uppercase tracking-wide border px-1.5 py-0.5 inline-block mt-1 ${cls}`}
         data-testid={`reminder-log-${user.id}`}
         title={`Last reminder · ${latest.label} · ${latest.ok ? "delivered" : "failed"} · ${latest.ts}`}>
      {latest.ok ? "✓" : "✗"} {latest.label} · {short}
    </div>
  );
}


// Iter70 · Admin listings manager — full list with per-row Unpublish + Republish
// buttons. Unpublishing prompts for a reason (emailed to the supplier) and, if
// the supplier has an active Stripe subscription, offers to issue a refund.
function ListingsManager() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [statusFilter, setStatusFilter] = React.useState("");
  const [q, setQ] = React.useState("");
  const [busy, setBusy] = React.useState(null);
  const [refundPrompt, setRefundPrompt] = React.useState(null);
  const [verifOpen, setVerifOpen] = React.useState(null);
  const [editListing, setEditListing] = React.useState(null);
  // Iter71 · bulk selection
  const [selected, setSelected] = React.useState(new Set());
  const [bulkBusy, setBulkBusy] = React.useState(false);
  const toggleSel = (id) => {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleSelAll = (visible) => {
    setSelected((s) => {
      const allSelected = visible.every((l) => s.has(l.id));
      if (allSelected) return new Set([...s].filter((id) => !visible.find((l) => l.id === id)));
      const n = new Set(s); visible.forEach((l) => n.add(l.id)); return n;
    });
  };

  const [loadError, setLoadError] = React.useState(null);
  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (q) params.set("q", q);
      const { data } = await api.get(`/admin/listings?${params.toString()}`);
      setItems(data.items || []);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      setLoadError({
        status,
        message: status === 401
          ? "Your admin session isn't being recognised (401). Sign in again — if this keeps happening, check you're on https://accommodationsuppliers.com and not a mix of www/apex."
          : status === 403 ? "This account doesn't have admin access (403)."
          : detail || (status ? `Failed to load listings (${status})` : "Failed to load listings — network error"),
      });
      toast.error(detail || (status === 401 ? "Session expired or not recognised" : "Failed to load listings"));
    } finally { setLoading(false); }
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const unpublish = async (l) => {
    const reason = window.prompt(
      `Unpublish "${l.title}"?\n\nThe listing hides from the public directory immediately, and the supplier gets an email explaining why.\n\nReason (shown to the supplier):`,
      ""
    );
    if (reason === null) return;
    setBusy(l.id);
    try {
      const { data } = await api.post(`/admin/listings/${l.id}/unpublish`, { reason });
      toast.success(`Unpublished · supplier ${data.email_sent ? "was emailed" : "email failed"}`);
      load();
      if (data.supplier_has_active_subscription) {
        setRefundPrompt({
          supplier_id:    data.supplier_id,
          supplier_email: data.supplier_email,
          listing_title:  l.title,
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to unpublish");
    } finally { setBusy(null); }
  };

  const republish = async (l) => {
    setBusy(l.id);
    try {
      await api.post(`/admin/listings/${l.id}/republish`);
      toast.success(`"${l.title}" is live again`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to republish");
    } finally { setBusy(null); }
  };

  const bulkUnpublish = async () => {
    const ids = [...selected].filter((id) => {
      const l = items.find((x) => x.id === id);
      return l && l.status !== "unpublished";
    });
    if (ids.length === 0) { toast.error("Selected rows are already unpublished"); return; }
    const reason = window.prompt(
      `Bulk unpublish ${ids.length} listing${ids.length === 1 ? "" : "s"}?\n\n` +
      `Each supplier will receive the removal email with the reason below.\n\nReason:`,
      ""
    );
    if (reason === null) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/admin/listings/bulk-unpublish`, { ids, reason });
      toast.success(`Unpublished ${data.updated} · ${data.emails_sent} emails sent`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk unpublish failed");
    } finally { setBulkBusy(false); }
  };

  const bulkRepublish = async () => {
    const ids = [...selected].filter((id) => {
      const l = items.find((x) => x.id === id);
      return l && l.status === "unpublished";
    });
    if (ids.length === 0) { toast.error("Selected rows aren't unpublished"); return; }
    if (!window.confirm(`Republish ${ids.length} listing${ids.length === 1 ? "" : "s"}?\n\nEach supplier will get the 'back live' email.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/admin/listings/bulk-republish`, { ids });
      toast.success(`Republished ${data.updated} · ${data.emails_sent} emails sent`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk republish failed");
    } finally { setBulkBusy(false); }
  };

  const statusPill = (s) => {
    const map = {
      active:        "bg-emerald-100 text-emerald-800 border-emerald-300",
      unpublished:   "bg-red-100 text-red-800 border-red-300",
      pending:       "bg-amber-100 text-amber-800 border-amber-300",
      paused_billing:"bg-amber-100 text-amber-800 border-amber-300",
    };
    return <span className={`text-[10px] uppercase tracking-widest font-semibold border px-2 py-0.5 ${map[s] || "bg-stone-100 text-stone-700 border-stone-300"}`}>{s || "—"}</span>;
  };

  return (
    <div data-testid="admin-listings-manager">
      {editListing && <ListingForm listing={editListing} onClose={() => setEditListing(null)} onSaved={load} />}
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div>
          <label className="block text-xs text-[#7D8C85] mb-1">Search</label>
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(); }} placeholder="Title or company" className="px-3 py-2 border border-[#C4CFC9] bg-white text-sm rounded-sm" data-testid="admin-listings-search" />
        </div>
        <div>
          <label className="block text-xs text-[#7D8C85] mb-1">Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border border-[#C4CFC9] bg-white text-sm rounded-sm" data-testid="admin-listings-status-filter">
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="unpublished">Unpublished</option>
            <option value="paused_billing">Paused (billing)</option>
          </select>
        </div>
        <button onClick={load} className="btn-primary" data-testid="admin-listings-refresh">Search</button>
        {selected.size > 0 && (
          <>
            <button
              onClick={bulkUnpublish}
              disabled={bulkBusy}
              className="text-xs uppercase tracking-widest font-semibold bg-[#B35C4A] text-white hover:bg-[#9E4E3E] px-3 py-2 transition disabled:opacity-50"
              data-testid="admin-listings-bulk-unpublish"
            >
              {bulkBusy ? "…" : `Unpublish ${selected.size}`}
            </button>
            <button
              onClick={bulkRepublish}
              disabled={bulkBusy}
              className="text-xs uppercase tracking-widest font-semibold bg-emerald-600 text-white hover:bg-emerald-700 px-3 py-2 transition disabled:opacity-50"
              data-testid="admin-listings-bulk-republish"
            >
              {bulkBusy ? "…" : `Republish ${selected.size}`}
            </button>
          </>
        )}
        <div className="text-xs text-[#7D8C85] ml-auto" data-testid="admin-listings-count">{loadError ? "—" : `${items.length} listing${items.length === 1 ? "" : "s"}`}</div>
      </div>

      {loadError && (
        <div className="mb-4 border border-[#B35C4A] bg-[#FBECE8] text-[#B35C4A] px-4 py-3 text-sm flex flex-wrap items-center gap-3" role="alert" data-testid="admin-listings-error">
          <span className="flex-1">{loadError.message}</span>
          <button onClick={load} className="text-[10px] uppercase tracking-widest font-semibold border border-[#B35C4A] px-2 py-1 hover:bg-[#B35C4A] hover:text-white transition" data-testid="admin-listings-retry">Retry</button>
          {loadError.status === 401 && <a href="/login" className="text-[10px] uppercase tracking-widest font-semibold bg-[#B35C4A] text-white px-2 py-1" data-testid="admin-listings-signin">Sign in again</a>}
        </div>
      )}

      <div className="bg-white border border-[#E8ECE9] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F0EFEA] text-[#4A5550]">
            <tr className="text-left">
              <th className="px-3 py-2 w-8">
                <input
                  type="checkbox"
                  onChange={() => toggleSelAll(items)}
                  checked={items.length > 0 && items.every((l) => selected.has(l.id))}
                  data-testid="admin-listings-select-all"
                  aria-label="Select all"
                />
              </th>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Supplier</th>
              <th className="px-3 py-2">Tier</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Verification</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-3 py-8 text-center text-[#7D8C85]">Loading…</td></tr>}
            {!loading && !loadError && items.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-[#7D8C85]">No listings match.</td></tr>}
            {!loading && loadError && <tr><td colSpan={7} className="px-3 py-8 text-center text-[#B35C4A]" data-testid="admin-listings-error-row">Listings could not be loaded{loadError.status ? ` (HTTP ${loadError.status})` : ""}.</td></tr>}
            {items.map((l) => (
              <React.Fragment key={l.id}>
              <tr className={`border-t border-[#F0EFEA] ${selected.has(l.id) ? "bg-[#FAFAF7]" : ""}`} data-testid={`admin-listing-${l.id}`}>
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selected.has(l.id)}
                    onChange={() => toggleSel(l.id)}
                    data-testid={`admin-listing-check-${l.id}`}
                  />
                </td>                <td className="px-3 py-2">
                  <div className="font-medium text-[#11211A]">{l.title || "Untitled"}</div>
                  <div className="text-[11px] text-[#7D8C85]">{(l.created_at || "").slice(0, 10)}</div>
                </td>
                <td className="px-3 py-2 text-[#4A5550]">
                  <div>{l.company || l.advertiser_name || "—"}</div>
                  <div className="text-[11px] text-[#7D8C85]">{l.supplier_email}</div>
                </td>
                <td className="px-3 py-2 text-[#4A5550]">{l.supplier_tier || l.tier || "free"}</td>
                <td className="px-3 py-2">{statusPill(l.status)}</td>
                <td className="px-3 py-2" data-testid={`admin-listing-verification-${l.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <VerificationStatusPill status={l.verification_status} verified={l.supplier_is_verified} />
                    <button onClick={() => setVerifOpen(verifOpen === l.id ? null : l.id)} className="text-[10px] text-[#264839] underline underline-offset-2" data-testid={`admin-listing-verification-edit-${l.id}`}>Verification</button>
                  </div>
                  {l.business_registration_number && (
                    <div className="text-[11px] text-[#4A5550] mt-1" data-testid={`admin-listing-reg-${l.id}`}>
                      <span className="uppercase tracking-widest text-[9px] text-[#7D8C85] mr-1">{l.business_registration_number_type}</span>{l.business_registration_number}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1.5 flex-wrap">
                    <button onClick={() => setEditListing(l)} className="text-[10px] uppercase tracking-widest font-semibold bg-[#264839] text-white px-2 py-1 hover:bg-[#1B342A] transition inline-flex items-center gap-1" data-testid={`admin-listing-edit-${l.id}`}><Pencil className="w-3 h-3" /> Edit</button>
                    <a href={`/listing/${l.slug || l.id}`} target="_blank" rel="noopener noreferrer" className="text-[10px] uppercase tracking-widest font-semibold border border-[#264839] text-[#264839] px-2 py-1 hover:bg-[#264839] hover:text-white transition" data-testid={`admin-listing-preview-${l.id}`}>Preview</a>
                    {l.status !== "unpublished" && (
                      <button
                        onClick={() => unpublish(l)}
                        disabled={busy === l.id}
                        className="text-[10px] uppercase tracking-widest font-semibold bg-[#B35C4A] text-white hover:bg-[#9E4E3E] px-2 py-1 transition disabled:opacity-50"
                        data-testid={`admin-listing-unpublish-${l.id}`}
                      >
                        Unpublish
                      </button>
                    )}
                    {l.status === "unpublished" && (
                      <button
                        onClick={() => republish(l)}
                        disabled={busy === l.id}
                        className="text-[10px] uppercase tracking-widest font-semibold bg-emerald-600 text-white hover:bg-emerald-700 px-2 py-1 transition disabled:opacity-50"
                        data-testid={`admin-listing-republish-${l.id}`}
                      >
                        Republish
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              {verifOpen === l.id && (
                <tr className="bg-[#FAFAF7]"><td colSpan={7} className="px-3 py-3">
                  <VerificationEditor target={{ ...l, listing_id: l.id, is_verified: l.supplier_is_verified }} onDone={() => { setVerifOpen(null); load(); }} onCancel={() => setVerifOpen(null)} />
                </td></tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {refundPrompt && (
        <RefundPromptModal
          supplierId={refundPrompt.supplier_id}
          supplierEmail={refundPrompt.supplier_email}
          listingTitle={refundPrompt.listing_title}
          onClose={() => setRefundPrompt(null)}
        />
      )}
    </div>
  );
}


// Iter70 · Post-unpublish prompt to offer the supplier a Stripe refund.
function RefundPromptModal({ supplierId, supplierEmail, listingTitle, onClose }) {
  const [amount, setAmount] = React.useState("");
  const [note, setNote]     = React.useState("");
  const [busy, setBusy]     = React.useState(false);
  const [done, setDone]     = React.useState(null);

  const submit = async (fullRefund) => {
    setBusy(true);
    try {
      const body = { note };
      if (!fullRefund && amount) body.amount_cents = Math.round(parseFloat(amount) * 100);
      const { data } = await api.post(`/admin/suppliers/${supplierId}/refund`, body);
      setDone(data);
      toast.success(`Refund issued · ${data.currency?.toUpperCase()} ${((data.amount || 0) / 100).toFixed(2)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center px-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="refund-prompt-modal">
      <div className="bg-white max-w-md w-full p-6 shadow-2xl border-l-4 border-[#B35C4A]">
        <div className="tracking-overline text-[10px] text-[#B35C4A] mb-1">Active subscription detected</div>
        <h3 className="font-serif text-xl text-[#11211A] mb-1">Issue a Stripe refund?</h3>
        <div className="text-xs text-[#7D8C85] mb-4">
          The supplier <strong>{supplierEmail}</strong> had listing <em>{listingTitle}</em> unpublished.
          They have a live subscription — would you like to refund their most recent invoice?
        </div>
        {done ? (
          <div className="p-4 bg-emerald-50 border border-emerald-300">
            <div className="text-sm text-[#264839]">✓ Refund issued — Stripe ID <code>{done.refund_id}</code></div>
            <button onClick={onClose} className="btn-primary mt-3 text-xs" data-testid="refund-close">Close</button>
          </div>
        ) : (
          <>
            <label className="block text-xs uppercase tracking-widest text-[#264839] mb-1 font-semibold">Amount to refund (blank = full)</label>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm text-[#7D8C85]">$</span>
              <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Leave empty for full refund" className="flex-1 px-3 py-2 border border-[#C4CFC9] text-sm bg-white" data-testid="refund-amount" />
            </div>
            <label className="block text-xs uppercase tracking-widest text-[#264839] mb-1 font-semibold">Internal note</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} className="w-full px-3 py-2 border border-[#C4CFC9] text-sm bg-white mb-4" data-testid="refund-note" />
            <div className="flex gap-2 justify-end">
              <button onClick={onClose} className="btn-secondary text-xs" data-testid="refund-skip">Skip refund</button>
              <button onClick={() => submit(true)}  disabled={busy} className="btn-secondary text-xs disabled:opacity-50" data-testid="refund-full">{busy ? "…" : "Refund full invoice"}</button>
              <button onClick={() => submit(false)} disabled={busy || !amount} className="text-xs uppercase tracking-widest font-semibold bg-[#B35C4A] text-white hover:bg-[#9E4E3E] px-3 py-2 transition disabled:opacity-50" data-testid="refund-partial">
                {busy ? "Refunding…" : "Refund partial amount"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// Iter71 · Refund History — audit log aggregated from users.admin_refunds.
// Iter72 · adds date-range + supplier filters and CSV export.
function RefundsManager() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [q, setQ] = React.useState("");
  const [fromDate, setFromDate] = React.useState("");
  const [toDate, setToDate] = React.useState("");

  const [loadError, setLoadError] = React.useState(null);
  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (q)         params.set("q", q);
      if (fromDate)  params.set("from_date", fromDate);
      if (toDate)    params.set("to_date", toDate);
      const { data } = await api.get(`/admin/refunds?${params.toString()}`);
      setItems(data.items || []);
    } catch (e) {
      setItems([]);
      setLoadError(describeLoadError(e, "refund history"));
      toast.error(e.response?.data?.detail || "Failed to load refund history");
    } finally { setLoading(false); }
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const totalRefunded = items.reduce((sum, r) => sum + (r.amount || 0), 0);

  const csvEscape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const downloadCsv = () => {
    if (items.length === 0) { toast.error("Nothing to export"); return; }
    const headers = ["Date", "Supplier company", "Supplier name", "Supplier email", "Currency", "Amount", "Stripe refund ID", "Invoice ID", "Note"];
    const rows = items.map((r) => [
      (r.created_at || "").replace("T", " ").slice(0, 19),
      r.supplier_company || "",
      r.supplier_name    || "",
      r.supplier_email   || "",
      (r.currency || "USD").toUpperCase(),
      ((r.amount || 0) / 100).toFixed(2),
      r.refund_id   || "",
      r.invoice_id  || "",
      r.note        || "",
    ].map(csvEscape).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `refunds-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-refunds-manager">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="font-serif text-3xl text-[#11211A]">Refund history</div>
          <p className="text-xs text-[#7D8C85] mt-1">
            {items.length} record{items.length === 1 ? "" : "s"} ·
            <span className="ml-1 text-[#264839] font-semibold">Total: ${(totalRefunded / 100).toFixed(2)}</span>
          </p>
        </div>
        <button
          onClick={downloadCsv}
          disabled={items.length === 0}
          className="text-xs uppercase tracking-widest font-semibold border border-[#264839] text-[#264839] px-3 py-1.5 hover:bg-[#264839] hover:text-white transition disabled:opacity-40"
          data-testid="admin-refunds-csv"
        >
          ↓ Download CSV
        </button>
      </div>

      <div className="flex flex-wrap gap-3 items-end mb-4 bg-[#F0EFEA] p-3">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-[#7D8C85] mb-1">Supplier</label>
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(); }} placeholder="Name, email or company" className="px-3 py-2 border border-[#C4CFC9] bg-white text-sm rounded-sm" data-testid="admin-refunds-search" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-[#7D8C85] mb-1">From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="px-3 py-2 border border-[#C4CFC9] bg-white text-sm rounded-sm" data-testid="admin-refunds-from" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-[#7D8C85] mb-1">To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="px-3 py-2 border border-[#C4CFC9] bg-white text-sm rounded-sm" data-testid="admin-refunds-to" />
        </div>
        <button onClick={load} className="btn-primary" data-testid="admin-refunds-apply">Apply</button>
        <button
          onClick={() => { setQ(""); setFromDate(""); setToDate(""); setTimeout(load, 0); }}
          className="text-xs text-[#7D8C85] underline"
          data-testid="admin-refunds-clear"
        >
          Clear
        </button>
      </div>

      {loading && <div className="py-8 text-center text-[#7D8C85]">Loading…</div>}
      <LoadErrorBanner error={loadError} onRetry={load} testId="admin-refunds-error" />
      {!loading && !loadError && items.length === 0 && (
        <div className="bg-[#F0EFEA] p-12 text-center text-[#7D8C85]" data-testid="admin-refunds-empty">
          No refunds match the current filters.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="bg-white border border-[#E8ECE9] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F0EFEA] text-[#4A5550]">
              <tr className="text-left">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Supplier</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2">Stripe refund</th>
                <th className="px-3 py-2">Note</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.refund_id || r.created_at} className="border-t border-[#F0EFEA]" data-testid={`admin-refund-${r.refund_id}`}>
                  <td className="px-3 py-2 text-[#7D8C85] tabular-nums">{(r.created_at || "").replace("T", " ").slice(0, 16)}</td>
                  <td className="px-3 py-2 text-[#4A5550]">
                    <div>{r.supplier_company || r.supplier_name || "—"}</div>
                    <div className="text-[11px] text-[#7D8C85]">{r.supplier_email}</div>
                  </td>
                  <td className="px-3 py-2 text-right text-[#11211A] tabular-nums font-semibold">
                    {(r.currency || "USD").toUpperCase()} {((r.amount || 0) / 100).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-[#7D8C85] font-mono break-all">{r.refund_id}</td>
                  <td className="px-3 py-2 text-[#4A5550] text-[12px]">{r.note || <span className="text-[#C4CFC9]">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

